import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Alert,
  Box,
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IStripeClientSecretPayload, verifyCoupon } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import TrustBadge from '../../../images/content/stripe-badge-transparent.png';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { Loader } from '../../auth/components/OtpPageLayout';
import { useCustomPlanStore } from '../store/customPlanStore';
import { ICouponData } from '../types/ICoupon';
import { IPaymentInfo } from '../types/IPaymentInfo';
import { getPlanCode, getNoOfMonthFromInterval } from '../utils/helper';

import CouponLisiting from './CouponLisiting';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: WsColors.TertiaryShades100,
    color: WsColors.GenericRichBlack,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AlingTextSideBySide = ({
  leftText,
  rightText,
  leftTextCaption,
}: {
  leftText: string;
  rightText: string;
  leftTextCaption?: string;
}) => (
  <Grid container mt={2}>
    <Grid item xs={6} textAlign="left">
      <WsTypography bold> {leftText}</WsTypography>
      {leftTextCaption && (
        <WsTypography bold variant={WsTypoVariants.BODY2} color={WsTypoColors.QUINARY}>
          {leftTextCaption}
        </WsTypography>
      )}
    </Grid>
    <Grid item xs={6} textAlign="right">
      <WsTypography bold>{rightText}</WsTypography>
    </Grid>
    <Divider sx={{ width: '100%', mt: 1 }} />
  </Grid>
);

const PaymentCalculation = ({
  plan,
  onUserAcceptance,
}: {
  plan: IPaymentInfo;
  onUserAcceptance: (data: IStripeClientSecretPayload) => void;
}) => {
  const [loaded, setLoaded] = useState(false);
  const planStartDate = dayjs(new Date()).format('MMM DD');
  const endDate = dayjs(new Date()).add(getNoOfMonthFromInterval(plan?.time?.toString()), 'month');
  const planEndDate = dayjs(endDate).format('MMM DD');
  const customPlanData = useCustomPlanStore((state) => state.customPrice);
  const [totalCharge, setTotalCharge] = useState(plan.totalPrice);
  const [customPlanCoupon, setCustomPlanCoupon] = useState<string | null>(null);
  const [coupon, setCoupon] = useState<ICouponData | null>(null);
  const [tempUsers, setTempUsers] = useState(0);
  const [customPlanDiscount, setCustomPlanDiscount] = useState<number>(0);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      users: 2,
    },
  });

  const { setValue, watch, getValues } = methods;

  const totalUsers = watch('users');

  useEffect(() => {
    if (totalUsers > 0 && totalUsers !== tempUsers) {
      setTempUsers(totalUsers);
      const perUserCharge = totalUsers * plan.totalPrice;
      const discount: number = coupon?.code
        ? (perUserCharge * (coupon.coupon.percent_off ?? 0)) / 100
        : customPlanDiscount;
      setTotalCharge(perUserCharge - parseFloat(discount.toFixed(2)));
    }
  }, [coupon, customPlanDiscount, plan.totalPrice, tempUsers, totalCharge, totalUsers]);

  useEffect(() => {
    (async () => {
      if (customPlanData && plan.plan === 'Enterprise') {
        const discount = await verifyCoupon(customPlanData);
        if (discount.data.valid) {
          setCustomPlanCoupon(discount.data.discount);
          const off = (plan.totalPrice * totalUsers * parseFloat(discount.data.discount)) / 100;
          setCustomPlanDiscount(parseFloat(off.toFixed(2)));
          setTotalCharge(plan.totalPrice * totalUsers - parseFloat(off.toFixed(2)));
        }
      } else if (plan.customPlanCoupon) {
        const discount = await verifyCoupon(plan.customPlanCoupon);
        if (discount.data.valid) {
          setCustomPlanCoupon(discount.data.discount);
          const off = (plan.totalPrice * parseFloat(discount.data.discount)) / 100;
          setCustomPlanDiscount(parseFloat(off.toFixed(2)));
          setTotalCharge(plan.totalPrice - parseFloat(off.toFixed(2)));
        }
      }
      setLoaded(true);
    })();
  }, [customPlanData, plan.customPlanCoupon, plan.plan, plan.totalPrice, totalUsers]);

  const onInputChange = (e) => {
    const { value } = e.target;
    if (value < 2) {
      setNotification(failed('Users cannot be less then 0'));
      setValue('users', 2);
    }
  };

  const onCouponSubmit = (couponData: ICouponData) => {
    if (couponData.coupon.percent_off) {
      if (coupon?.code != couponData.code) {
        setCoupon(couponData);
        if (!isEmpty(coupon)) {
          const charge = totalUsers > 0 ? totalUsers * plan.totalPrice : 0;
          const discount: number = (charge * couponData.coupon.percent_off) / 100;
          setTotalCharge(charge - parseFloat(discount.toFixed(2)));
        } else {
          const charge = totalCharge;
          const discount: number = (charge * couponData.coupon.percent_off) / 100;
          setTotalCharge(charge - parseFloat(discount.toFixed(2)));
        }
      } else {
        setNotification(failed('This coupon is already applied'));
      }
    } else {
      setNotification(failed('No Discount Available'));
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    const perUserCharge = totalUsers > 0 ? totalUsers * plan.totalPrice : 0;
    setTotalCharge(perUserCharge);
  };

  const handleCoupon = () => removeCoupon();

  const onSubmit = () => {
    const planInfo = getPlanCode(plan?.plan);
    if (!isEmpty(planInfo)) {
      onUserAcceptance({
        amount: plan.totalPrice,
        productId: plan.productId,
        coupon: plan.customPlanCoupon ?? coupon?.coupon.name,
        interval: plan.time,
        customerId: plan.customerId,
        users: getValues('users')?.toString() ?? '0',
        manual: plan.manual,
        planCode: planInfo,
        userManagement: false,
      });
    } else {
      setNotification(failed('Unable to detect plan'));
    }
  };

  return (
    <>
      {loaded ? (
        <Grid container>
          <FormProvider {...methods}>
            <Grid item xs={12} textAlign="center">
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold>
                Your Cart
              </WsTypography>
            </Grid>
            <Grid item xs={12} mt={3}>
              <TableContainer>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>
                        <WsTypography bold>S.no</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Plan</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Price Per User</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Number of Users</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Total</WsTypography>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          1.
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {plan.plan}
                        </WsTypography>
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {`${planStartDate} - ${planEndDate}, ${dayjs(endDate).format('YYYY')}`}
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {plan.totalPrice}
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <ControlledTextInput
                          onMouseLeave={onInputChange}
                          name="users"
                          labelText=""
                          type="number"
                          fullWidth
                          id="users"
                          placeholder=""
                          InputProps={{ inputProps: { min: 2 } }}
                          variant="outlined"
                          autoComplete="users"
                          sx={{ width: 150 }}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {`$ ${totalUsers * plan.totalPrice}`}
                        </WsTypography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={6}>
              <>
                {(!customPlanCoupon || plan.plan != 'Enterprise') && (
                  <CouponLisiting onCouponSubmit={onCouponSubmit} />
                )}
                <Box sx={{ mt: plan.plan === 'Enterprise' ? '47%' : 0 }}>
                  <img
                    src={TrustBadge}
                    alt="Guaranteed safe & secure checkout"
                    style={{ height: '38px' }}
                  />
                </Box>
              </>
            </Grid>
            <Grid item xs={2}></Grid>
            <Grid item xs={4} mt={plan.plan != 'Enterprise' ? (coupon?.code ? 10 : 14) : 0}>
              <AlingTextSideBySide
                leftText="Subtotal:"
                rightText={`$ ${plan.totalPrice * totalUsers}`}
              />
              <AlingTextSideBySide leftText="Sales Tax:" rightText={`$ 0`} />
              {customPlanCoupon || plan.plan === 'Enterprise' ? (
                <AlingTextSideBySide
                  leftText="Total Discount:"
                  rightText={`${customPlanCoupon ? customPlanCoupon : 0}%`}
                />
              ) : (
                <>
                  {coupon?.code ? (
                    <>
                      <Alert
                        sx={{ cursor: 'pointer', mt: 1 }}
                        onClick={handleCoupon}
                        severity={coupon?.code ? 'success' : 'info'}
                        action={
                          <HighlightOffIcon
                            sx={{ color: 'red', cursor: 'pointer' }}
                            onClick={removeCoupon}
                          />
                        }
                      >
                        <WsTypography bold>{`'${coupon.code}' applied`}</WsTypography>
                        <WsTypography bold variant={WsTypoVariants.BODY2}>
                          {`Enjoy your ${coupon?.coupon.percent_off}% off`}
                        </WsTypography>
                      </Alert>
                      <Divider sx={{ width: '100%', mt: 1 }} />
                    </>
                  ) : (
                    <Grid container mt={1}>
                      <Grid item xs={6} textAlign="left">
                        <Box mt={0.5}>
                          <WsTypography bold> Coupon</WsTypography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <WsTypography bold>---</WsTypography>
                      </Grid>
                      <Divider sx={{ width: '100%', mt: 1 }} />
                    </Grid>
                  )}
                  <AlingTextSideBySide
                    leftText="Total Discount:"
                    rightText={`${coupon?.coupon?.percent_off ? coupon?.coupon?.percent_off : 0}%`}
                  />
                </>
              )}
              <AlingTextSideBySide
                leftText="Grand Total:"
                rightText={
                  totalCharge
                    ? Number.isInteger(totalCharge)
                      ? `$ ${totalCharge?.toString()}.00`
                      : `$ ${totalCharge?.toString()}`
                    : '00.00'
                }
              />
              <Stack mt={2}>
                <WsButton size="large" disabled={totalUsers < 2} onClick={onSubmit}>
                  Pay Now
                </WsButton>
              </Stack>
            </Grid>
          </FormProvider>
        </Grid>
      ) : (
        <Box sx={{ height: '400px' }}>
          <Loader />
        </Box>
      )}
    </>
  );
};

export default PaymentCalculation;
